// ============================================================
// KF CONSUMER — visualEngine.config.ts (configuración del tenant)
// ============================================================
// ÚNICO lugar de conocimiento de negocio del consumidor KF.
// El motor nunca cambia; el tenant configura. (P4/P6)
// El consumidor describe (contenido, contexto, config); el motor decide.
// F2: valores = comportamiento actual certificado (paridad absoluta).
// ============================================================
import type { TenantVisualConfig } from '../Common/visual-engine';
import type { TenantPieConfig } from '../Common/presentation-engine';

/** Config del feed (ProductCard) — F3: decisiones del motor gobernando el render */
export const kfFeedConfig: TenantVisualConfig = {
  fitPolicy: 'contain',
  mediaRatios: { default: '3/4', heavy: '1/1' },
  capabilities: {
    // Feed certificado: sin galería, sin contador, sin poster, sin controls
    gallery: false,
    counter: false,
    poster: false,
    controls: false,
    placeholder: true,
  },
  infoTreatment: {
    clampLines: 3,
    expandable: true,
  },
  presentation: {
    mediaBgClass: 'bg-gray-100',
    interactive: true,
    thumbActiveClass: 'border-gray-800 ring-2 ring-gray-300',
    thumbIdleClass: 'border-gray-200 hover:border-gray-400',
    videoThumbFallback: '',
  },
  texts: {
    expandLabel: 'Ver más',
    collapseLabel: 'Ver menos',
  },
};

/** Config PIE del feed (ProductCard → PieComposer) — PIE-I2:
    goals ponderados + constraints validados por simulación (74/74 canon-ratio).
    El motor elige el frame; el baseline sigue siendo el piso (P9). */
export const kfPieConfig: TenantPieConfig = {
  goals: [
    { id: 'space', weight: 0.35 },   // aprovechamiento: 34.8% → 13.9% (validado)
    { id: 'consistency', weight: 0.30, params: { canonRatios: ['1/2', '3/4'] } },
    { id: 'harmony', weight: 0.20 },  // armonía percibida (P4)
    { id: 'breathing', weight: 0.15, params: { breathingMin: 0.03, breathingMax: 0.12 } },
  ],
  constraints: {
    allowedRatios: ['3/4', '1/2', '1/1', 'real'],
    maxLetterboxRatio: 0.45,
    expandStyle: { mode: 'blur', fallbackColor: '#f3f4f6' },  // I3 (no activo en I2)
  },
  capabilities: {
    gallery: false,
    counter: false,
    poster: false,
    controls: false,
    placeholder: true,
  },
  texts: {
    expandLabel: 'Ver más',
    collapseLabel: 'Ver menos',
  },
  baselineMargin: 0.05,   // early-exit: baseline gana por ≥5% → no re-optimizar
  stability: {            // P10 — Visual Stability
    transitionMs: 300,
    respectReducedMotion: true,
  },
};

/** Config del detalle (ProductViewer) — F2: paridad total con el viewer inline actual */
export const kfDetailConfig: TenantVisualConfig = {
  fitPolicy: 'contain',
  mediaRatios: { default: '1/1', heavy: '1/1' },
  capabilities: {
    gallery: true,
    counter: true,
    poster: true,
    controls: true,
    placeholder: true,
  },
  presentation: {
    mediaBgClass: 'bg-black',
    interactive: false, // el detalle no escala (paridad con viewer inline)
    mediaContainerClass: 'rounded-lg',
    thumbActiveClass: 'border-purple-600 ring-2 ring-purple-300',
    thumbIdleClass: 'border-gray-200 hover:border-gray-400',
    videoThumbFallback: '/logos/video-placeholder.png',
  },
  texts: { counter: '{n} archivo(s) • {m} video(s)' },
};
